import React, { createContext, useContext, useState } from 'react';
import ToastNotification from './ToastNotification';

// Create context
const ToastContext = createContext();

/**
 * Toast Provider Component
 * Wrap your App with this to use toast notifications anywhere
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  // Show a toast notification
  const showToast = (message, type = 'success', duration = 5000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type, duration }]);

    // Auto-remove after duration
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  };

  // Remove a specific toast
  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      
      {/* Render all active toasts */}
      {toasts.map(toast => (
        <ToastNotification
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </ToastContext.Provider>
  );
}

/**
 * Hook to use toast notifications
 * @returns {Object} showToast function
 * @example
 * const { showToast } = useToast();
 * showToast('Data saved successfully!', 'success');
 * showToast('Error occurred', 'error');
 * showToast('Processing...', 'info');
 */
export function useToast() {
  const context = useContext(ToastContext);
  
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  
  return context;
}
